import {
  createAppBuildInfoUrl,
  parseJsonResponse
} from "../chunk-TNXG6CPU.js";
import {
  logDebug,
  logInfo,
  logResults
} from "../chunk-KTPUX2XH.js";
import {
  clientBuildNumber
} from "../chunk-YW3SPCXZ.js";
import "../chunk-X3MX6SC5.js";

// lib/updates/checkUpdates.tsx
import { Alert } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import semver from "semver";

// lib/updates/reload.ts
var cacheBustReload = async () => {
  if ("serviceWorker" in navigator) {
    const reg = await navigator.serviceWorker.getRegistration();
    if (reg?.waiting != null) {
      reg.waiting.postMessage("SKIP_WAITING");
      await new Promise((resolve) => {
        navigator.serviceWorker.addEventListener(
          "controllerchange",
          () => {
            resolve();
          },
          {
            once: true
          }
        );
      });
    }
  }
  location.reload();
};

// lib/updates/checkUpdates.tsx
import { jsx, jsxs } from "react/jsx-runtime";
var OnBeforeOpenResult = /* @__PURE__ */ ((OnBeforeOpenResult2) => {
  OnBeforeOpenResult2["OwnerWillHandleOpen"] = "ownerWillHandleOpen";
  return OnBeforeOpenResult2;
})(OnBeforeOpenResult || {});
var DEFAULT_CHECK_UPDATES_MS = 5 * 60 * 1e3;
var CheckUpdateErrors = {
  NotJsonResponse: new Error("response is not JSON"),
  MissingBuildNumber: new Error("field build.number is missing or empty"),
  MissingRootUrl: new Error("Missing 'rootUrl' on getServerBuild")
};
var getAppOnServerBuild = async ({
  rootUrl: url,
  cacheBustId
}) => {
  return await fetch(createAppBuildInfoUrl(url, cacheBustId).build.info.url).then(
    async (result) => await parseJsonResponse(result)
  ).then((result) => {
    if (typeof result !== "object") {
      throw CheckUpdateErrors.NotJsonResponse;
    }
    return result;
  }).catch((err) => {
    console.error("Can't find build info", err);
    throw err;
  });
};
var hasNewVersion = async ({
  rootUrl,
  myBuild,
  serverBuild = getAppOnServerBuild
}) => {
  if (myBuild === void 0) {
    return false;
  }
  return await serverBuild({ rootUrl }).then((serverBuild2) => {
    logResults("Check updates")({
      myBuild: myBuild.build.number,
      serverBuild: serverBuild2.build.number
    });
    const doesHaveNewVersion = semver.gt(
      serverBuild2.build.number,
      myBuild.build.number
    );
    if (doesHaveNewVersion) {
      logInfo(
        "New version available:",
        serverBuild2.build.number,
        `(current: ${myBuild.build.number})`
      );
    } else {
      logInfo("Version is current", myBuild.build.number, "on", /* @__PURE__ */ new Date());
    }
    return serverBuild2.build.number > myBuild.build.number;
  });
};
var NewVersionAlert = ({
  className,
  version,
  onReload
}) => /* @__PURE__ */ jsxs(Alert, { className, severity: "info", children: [
  "A new version (",
  version.build.number,
  ") is available. ",
  /* @__PURE__ */ jsx("br", {}),
  "Please",
  " ",
  /* @__PURE__ */ jsx(
    "a",
    {
      href: "",
      onClick: (e) => {
        e.preventDefault();
        if (onReload !== void 0) {
          onReload();
        } else {
          cacheBustReload();
        }
      },
      children: "reload"
    }
  ),
  "."
] });
var CheckUpdates = ({
  className,
  onReload,
  onBeforeOpen,
  checkEveryMs = DEFAULT_CHECK_UPDATES_MS,
  rootUrl,
  disabled = false,
  appOnServerBuildGetter = getAppOnServerBuild,
  clientBuildNumber: clientBuildNumber2 = clientBuildNumber
}) => {
  const [newVersionNumber, setNewVersionNumber] = useState(null);
  const [hasVersion, setHasVersion] = useState(false);
  const [toastVisible, setToastVisible] = useState(false);
  const beforeOpenFiredRef = useRef(false);
  const checkServerForUpdates = useCallback(
    (rootUrl2) => {
      logInfo("Checking for updates");
      appOnServerBuildGetter({ rootUrl: rootUrl2 }).then((version) => {
        logDebug(
          "Returned version",
          version.build.number,
          "current",
          newVersionNumber
        );
        if (newVersionNumber?.build.number !== version.build.number) {
          setNewVersionNumber(version);
        }
      }).catch((err) => {
        logDebug("Failed to get version", err);
        if (newVersionNumber !== null) {
          setNewVersionNumber(null);
        }
      });
      hasNewVersion({
        rootUrl: rootUrl2,
        myBuild: clientBuildNumber2(),
        serverBuild: appOnServerBuildGetter
      }).then((result) => {
        logDebug("Has new version?", result);
        if (hasVersion !== result) {
          setHasVersion(result);
        }
      }).catch((err) => {
        logDebug("Failed to detect new version", err);
        if (hasVersion !== false) {
          setHasVersion(false);
        }
      });
    },
    [appOnServerBuildGetter, clientBuildNumber2, hasVersion, newVersionNumber]
  );
  useEffect(() => {
    if (!hasVersion || newVersionNumber === null) return;
    if (beforeOpenFiredRef.current) return;
    beforeOpenFiredRef.current = true;
    if (onBeforeOpen === void 0) {
      setToastVisible(true);
      return;
    }
    const showToast = () => {
      setToastVisible(true);
    };
    const result = onBeforeOpen(showToast);
    if (result !== "ownerWillHandleOpen" /* OwnerWillHandleOpen */) {
      setToastVisible(true);
    }
  }, [hasVersion, newVersionNumber, onBeforeOpen]);
  useEffect(() => {
    if (disabled) {
      logInfo("Checking updates disabled");
      return;
    }
    checkServerForUpdates(rootUrl);
    logInfo(
      `Checking updates every ${checkEveryMs / 1e3} seconds. Next @ ${new Date(Date.now() + checkEveryMs).toLocaleTimeString()}`
    );
    const newTimer = setInterval(() => {
      checkServerForUpdates(rootUrl);
    }, checkEveryMs);
    return () => {
      clearInterval(newTimer);
    };
  }, [checkEveryMs, checkServerForUpdates, disabled, rootUrl]);
  return toastVisible && newVersionNumber !== null ? /* @__PURE__ */ jsx(
    NewVersionAlert,
    {
      className,
      onReload,
      version: newVersionNumber
    }
  ) : null;
};
export {
  CheckUpdateErrors,
  CheckUpdates,
  OnBeforeOpenResult,
  cacheBustReload,
  getAppOnServerBuild,
  hasNewVersion
};
